import React, { useState } from 'react';
import {
  Box, Typography, Paper, TextField, Button, Alert, CircularProgress,
  Chip, Grid, Card, CardContent
} from '@mui/material';
import { emissionFactorService } from '../services/emissionFactorService';

function EmissionFactorSearch() {
  const [query, setQuery] = useState('');
  const [geography, setGeography] = useState('');
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleSearch = async () => {
    if (!query.trim()) return;
    try {
      setLoading(true);
      setError(null);
      const data = await emissionFactorService.searchFactor(query, {
        geography: geography || undefined,
      });
      setResults(data);
    } catch (err) {
      setError(err.response?.data?.error?.message || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box>
      <Typography variant="h4" gutterBottom>Emission Factor RAG Search</Typography>
      <Typography variant="body1" color="text.secondary" paragraph>
        AI-powered semantic search over emission factor databases with 4-axis validation
      </Typography>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={6}>
            <TextField fullWidth label="Activity Description" placeholder="e.g., Diesel combustion in heavy machinery" value={query} onChange={(e) => setQuery(e.target.value)} onKeyPress={(e) => e.key === 'Enter' && handleSearch()} />
          </Grid>
          <Grid item xs={3}>
            <TextField fullWidth label="Geography Filter" placeholder="e.g., EU, US, Global" value={geography} onChange={(e) => setGeography(e.target.value)} />
          </Grid>
          <Grid item xs={3}>
            <Button variant="contained" fullWidth onClick={handleSearch} disabled={loading || !query.trim()} sx={{ height: 56 }}>
              {loading ? <CircularProgress size={24} /> : 'Search Factors'}
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {results && !results.found && (
        <Alert severity="info">No matching emission factors found for "{query}"</Alert>
      )}

      {results && results.found && (
        <>
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>Selected Factor</Typography>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">Factor Name</Typography>
                  <Typography>{results.selected_factor?.factor_name || results.selected_factor?.description_text || '-'}</Typography>
                </Grid>
                <Grid item xs={3}>
                  <Typography variant="body2" color="text.secondary">Value</Typography>
                  <Typography variant="h6">{results.selected_factor?.value_kg_co2e_per_unit} kgCO2e</Typography>
                </Grid>
                <Grid item xs={3}>
                  <Typography variant="body2" color="text.secondary">Confidence</Typography>
                  <Typography variant="h6">{((results.confidence || results.validation?.confidence || 0) * 100).toFixed(1)}%</Typography>
                </Grid>
              </Grid>

              <Typography variant="body2" sx={{ mt: 2 }}>{results.justification || results.validation?.justification}</Typography>

              {results.validation && (
                <Box sx={{ mt: 2, display: 'flex', gap: 1 }}>
                  {Object.entries(results.validation).filter(([k]) => k.includes('context') || k === 'source' || k === 'geography' || k === 'temporal' || k === 'methodology').map(([axis, val]) => (
                    <Chip key={axis} label={`${axis}: ${val?.valid ? 'Valid' : 'Invalid'}`} color={val?.valid ? 'success' : 'error'} size="small" />
                  ))}
                </Box>
              )}
            </CardContent>
          </Card>

          <Typography variant="subtitle2" color="text.secondary">
            {results.candidates_evaluated || results.candidates_count || 0} candidates evaluated | Model: {results.model_version}
          </Typography>
        </>
      )}
    </Box>
  );
}

export default EmissionFactorSearch;
